<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';

const { t } = useI18n();
const MOTORES = ['jev', 'ia'];
const carregando = ref(true);
const erro = ref('');
const dados = ref({
  caixas: [],
  quadros: [],
  decisoes: [],
  regras: '',
  mes: { decisoes: 0, custo_estimado: 0 },
});
const moeda = valor =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 4,
  }).format(valor || 0);
const regras = ref('');
const descricoes = ref({});
const salvando = ref(false);
const trocando = ref(null);

const falhou = e =>
  useAlert(e?.response?.data?.error || t('AGENTE_IA.OPERADOR.ERRO'));

const aplicar = data => {
  dados.value = data;
  regras.value = data.regras;
  descricoes.value = Object.fromEntries(
    data.quadros.flatMap(q => q.etapas.map(e => [e.id, e.description]))
  );
};

const carregar = async () => {
  try {
    aplicar((await AgenteIaAPI.operador()).data);
  } catch (e) {
    erro.value = e?.response?.data?.error || t('AGENTE_IA.OPERADOR.ERRO');
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const comJev = computed(() => dados.value.motor === 'jev');

const salvar = async (mudancas, aviso = true) => {
  salvando.value = true;
  try {
    aplicar((await AgenteIaAPI.salvarOperador(mudancas)).data);
    if (aviso) useAlert(t('AGENTE_IA.SALVO'));
  } catch (e) {
    falhou(e);
  } finally {
    salvando.value = false;
  }
};

const salvarEtapas = () =>
  salvar({
    etapas: Object.entries(descricoes.value).map(([id, description]) => ({
      id: Number(id),
      description,
    })),
  });

const sugerindo = ref(false);
const sugerir = async () => {
  sugerindo.value = true;
  try {
    aplicar((await AgenteIaAPI.sugerirFrases()).data);
  } catch (e) {
    falhou(e);
  } finally {
    sugerindo.value = false;
  }
};

const alternar = async caixa => {
  trocando.value = caixa.id;
  try {
    aplicar((await AgenteIaAPI.observarCaixa(caixa.id, !caixa.observada)).data);
  } catch (e) {
    falhou(e);
  } finally {
    trocando.value = null;
  }
};

const porcentagem = valor => `${Math.round((valor || 0) * 100)}%`;

// As acoes vem como "etapa:Fechado", "etiqueta:orcamento" ou "nota"; na tela, em texto de gente.
const acao = item => {
  const [tipo, valor] = item.split(/:(.*)/s);
  if (tipo === 'etapa')
    return t('AGENTE_IA.OPERADOR.DECISOES.MOVEU', { etapa: valor });
  if (tipo === 'etiqueta')
    return t('AGENTE_IA.OPERADOR.DECISOES.ETIQUETA', { etiqueta: valor });
  return t('AGENTE_IA.OPERADOR.DECISOES.NOTA');
};
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.OPERADOR.TITULO')"
    :description="t('AGENTE_IA.OPERADOR.DESCRICAO')"
  >
    <p v-if="carregando" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.CARREGANDO') }}
    </p>
    <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>
    <template v-else>
      <p class="text-sm text-n-slate-11">
        {{
          t(
            comJev
              ? 'AGENTE_IA.OPERADOR.AVISO_AGENTE.JEV'
              : 'AGENTE_IA.OPERADOR.AVISO_AGENTE.IA'
          )
        }}
      </p>

      <section class="flex flex-col gap-3">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.OPERADOR.MOTOR.TITULO') }}
        </h2>
        <div class="grid gap-2 sm:grid-cols-2">
          <button
            v-for="motor in MOTORES"
            :key="motor"
            type="button"
            class="flex flex-col gap-1 p-3 text-left rounded-xl outline outline-1"
            :class="
              dados.motor === motor
                ? 'outline-n-blue-7 bg-n-blue-2'
                : 'outline-n-weak hover:bg-n-alpha-1'
            "
            :disabled="salvando"
            @click="dados.motor !== motor && salvar({ motor })"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ t(`AGENTE_IA.OPERADOR.MOTOR.${motor.toUpperCase()}.NOME`) }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t(`AGENTE_IA.OPERADOR.MOTOR.${motor.toUpperCase()}.RESUMO`) }}
            </span>
          </button>
        </div>
      </section>

      <section v-if="comJev" class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.OPERADOR.MODO.TITULO') }}
          </h2>
          <p class="text-xs text-n-slate-11">
            {{
              t(
                dados.aplicar
                  ? 'AGENTE_IA.OPERADOR.MODO.APLICANDO'
                  : 'AGENTE_IA.OPERADOR.MODO.OBSERVANDO'
              )
            }}
          </p>
        </div>
        <div>
          <Button
            size="sm"
            :variant="dados.aplicar ? 'outline' : 'solid'"
            :is-loading="salvando"
            :label="
              t(
                dados.aplicar
                  ? 'AGENTE_IA.OPERADOR.MODO.VOLTAR'
                  : 'AGENTE_IA.OPERADOR.MODO.LIGAR'
              )
            "
            @click="salvar({ aplicar: !dados.aplicar })"
          />
        </div>
      </section>

      <section
        v-if="comJev"
        class="flex flex-col gap-2 p-4 rounded-lg bg-n-alpha-black2"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.OPERADOR.CUSTO.TITULO') }}
        </h2>
        <p class="text-xs text-n-slate-11">
          {{ t('AGENTE_IA.OPERADOR.CUSTO.INCLUIDO') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{
            t('AGENTE_IA.OPERADOR.CUSTO.MES', {
              decisoes: dados.mes.decisoes,
              custo: moeda(dados.mes.custo_estimado),
            })
          }}
        </p>
      </section>

      <section v-if="comJev" class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.OPERADOR.ETAPAS.TITULO') }}
          </h2>
          <p class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.OPERADOR.ETAPAS.AJUDA') }}
          </p>
        </div>
        <p v-if="!dados.quadros.length" class="text-sm text-n-slate-11">
          {{ t('AGENTE_IA.OPERADOR.ETAPAS.VAZIO') }}
        </p>
        <div
          v-for="quadro in dados.quadros"
          :key="quadro.id"
          class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ quadro.name }}
          </span>
          <label
            v-for="etapa in quadro.etapas"
            :key="etapa.id"
            class="flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-12">{{ etapa.name }}</span>
            <input
              v-model="descricoes[etapa.id]"
              class="px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
              :placeholder="t('AGENTE_IA.OPERADOR.ETAPAS.EXEMPLO')"
            />
          </label>
        </div>
        <div v-if="dados.quadros.length" class="flex flex-wrap gap-2">
          <Button
            :label="t('AGENTE_IA.OPERADOR.ETAPAS.SALVAR')"
            :is-loading="salvando"
            @click="salvarEtapas"
          />
          <Button
            variant="outline"
            :label="t('AGENTE_IA.OPERADOR.ETAPAS.SUGERIR')"
            :is-loading="sugerindo"
            @click="sugerir"
          />
        </div>
      </section>

      <section v-else class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.OPERADOR.REGRAS') }}
          </h2>
          <p class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.OPERADOR.REGRAS_AJUDA') }}
          </p>
        </div>
        <textarea
          v-model="regras"
          rows="5"
          class="px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
          :placeholder="t('AGENTE_IA.OPERADOR.REGRAS_EXEMPLO')"
        />
        <div class="flex items-center justify-between gap-3">
          <span v-if="dados.modelo" class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.OPERADOR.MODELO', { modelo: dados.modelo }) }}
          </span>
          <Button
            :label="t('AGENTE_IA.OPERADOR.SALVAR')"
            :is-loading="salvando"
            @click="salvar({ regras })"
          />
        </div>
      </section>

      <section class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.OPERADOR.CAIXAS') }}
          </h2>
          <p class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.OPERADOR.CAIXAS_AJUDA') }}
          </p>
        </div>
        <ul class="flex flex-col gap-2">
          <li
            v-for="caixa in dados.caixas"
            :key="caixa.id"
            class="flex items-center justify-between gap-4 p-3 border rounded-lg border-n-weak"
          >
            <div class="flex flex-col gap-0.5">
              <span class="text-sm text-n-slate-12">{{ caixa.name }}</span>
              <span
                v-if="caixa.observada && !caixa.cria_card"
                class="text-xs text-n-amber-11"
              >
                {{ t('AGENTE_IA.OPERADOR.SEM_FUNIL') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  t(
                    caixa.observada
                      ? 'AGENTE_IA.OPERADOR.OBSERVANDO'
                      : 'AGENTE_IA.OPERADOR.PARADO'
                  )
                }}
              </span>
            </div>
            <Button
              size="sm"
              :variant="caixa.observada ? 'outline' : 'solid'"
              :is-loading="trocando === caixa.id"
              :label="
                t(
                  caixa.observada
                    ? 'AGENTE_IA.OPERADOR.PARAR'
                    : 'AGENTE_IA.OPERADOR.OBSERVAR'
                )
              "
              @click="alternar(caixa)"
            />
          </li>
        </ul>
      </section>

      <section v-if="comJev" class="flex flex-col gap-3">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.OPERADOR.DECISOES.TITULO') }}
        </h2>
        <p v-if="!dados.decisoes.length" class="text-sm text-n-slate-11">
          {{ t('AGENTE_IA.OPERADOR.DECISOES.VAZIO') }}
        </p>
        <ul class="flex flex-col gap-1">
          <li
            v-for="decisao in dados.decisoes"
            :key="decisao.id"
            class="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm border rounded-lg border-n-weak"
          >
            <span class="text-n-slate-12">
              {{
                t(
                  decisao.apagada
                    ? 'AGENTE_IA.OPERADOR.DECISOES.LINHA_APAGADA'
                    : 'AGENTE_IA.OPERADOR.DECISOES.LINHA',
                  {
                    conversa: decisao.conversa,
                    de: decisao.etapa_atual,
                    para: decisao.etapa_sugerida,
                    certeza: porcentagem(decisao.confianca),
                  }
                )
              }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{
                decisao.aplicada
                  ? decisao.acoes.map(acao).join(' · ') ||
                    t('AGENTE_IA.OPERADOR.DECISOES.NADA')
                  : t('AGENTE_IA.OPERADOR.DECISOES.SO_OBSERVOU')
              }}
            </span>
          </li>
        </ul>
      </section>
    </template>
  </AgenteIaLayout>
</template>
